import { SharedService } from '../../shared/shared.service';
import { from, mergeMap, Observable, throwError } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { catchError, finalize } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { Router } from '@angular/router';
import { RoutesEnum } from '../utils/RoutesEnum';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AuthService } from "./auth.service";
import { OidcSecurityService } from "angular-auth-oidc-client";

export const InterceptorSkipHeader = 'X-Skip-Interceptor-Header';
export const InterceptorSkipLoader = 'X-Skip-Interceptor-Loader';

@Injectable( {
  providedIn: 'root'
} )
export class AuthInterceptorService implements HttpInterceptor {
  
  private sharedService = inject(SharedService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private nzMessageService = inject(NzMessageService);
  private oidcSecurityService = inject(OidcSecurityService);

  private totalRequests = 0;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipLoader = req.headers.has(InterceptorSkipLoader);

    let request = req;

    if (skipLoader) {
      request = request.clone({
        headers: request.headers.delete(InterceptorSkipLoader)
      });
    } else {
      this.totalRequests++;
      this.showLoader();
    }

    const isOidcRequest =
      request.url.includes('/userInfo') ||
      request.url.includes('/token') ||
      request.url.includes('/oauth2');

    const token$ = isOidcRequest
      ? from(this.oidcSecurityService.getAccessToken())
      : from(this.oidcSecurityService.getIdToken());

    return token$.pipe(
      mergeMap(token => {
        if (token) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });
        }

        return next.handle(request).pipe(
          catchError(err => this.handleError(err)),
          finalize(() => {
            if (!skipLoader) {
              this.decrementLoader();
            }
          })
        );
      })
    );
  }

  private handleError(err: any): Observable<never> {
    switch (err.status) {
      case 400:
        this.nzMessageService.error('Bad request.');
        break;
      case 401:
        this.nzMessageService.error('Unauthorized. Please log in again.');
        this.authService.logout();
        break;
      case 403:
        this.nzMessageService.error('Access denied.');
        break;
      case 404:
        this.nzMessageService.error('Page not found.');
        this.router.navigateByUrl(RoutesEnum.PageNotFound);
        break;
      case 409:
        this.nzMessageService.error('Conflict detected.');
        break;
      case 500:
        this.nzMessageService.error('Unexpected server error.');
        break;
      default:
        this.nzMessageService.error(
          'Something went wrong. Please try again later.'
        );
    }

    return throwError(() => err);
  }

  private decrementLoader(): void {
    this.totalRequests = Math.max(0, this.totalRequests - 1);
    if (this.totalRequests === 0) {
      this.hideLoader();
    }
  }

  private showLoader(): void {
    this.sharedService.showLoader();
  }

  private hideLoader(): void {
    this.sharedService.hideLoader();
  }
}
